import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiJson } from "../../shared/lib/api-client.ts";
import type { Toast } from "../../shared/types/scoring.ts";

type UserRole = "admin" | "basic";

interface AdminUser {
  uid: string;
  email: string | null;
  role: UserRole;
  status: "active" | "disabled";
  createdAt: string | null;
  lastSignInAt: string | null;
}

interface AdminDashboardViewProps {
  addToast: (type: Toast["type"], title: string, message: string) => void;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function AdminDashboardView({ addToast }: AdminDashboardViewProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("basic");
  const [submitting, setSubmitting] = useState(false);
  const [resettingUid, setResettingUid] = useState<string | null>(null);

  const userCountLabel = useMemo(() => `${users.length} users`, [users.length]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await apiJson<{ data?: AdminUser[] }>("/v1/admin/users");
      const list = payload.data ?? [];
      list.sort((a, b) => (a.email ?? "").localeCompare(b.email ?? ""));
      setUsers(list);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  async function sendReset(
    emailAddress: string,
    purpose: "invite" | "reset",
    successTitle: string,
    targetRole?: UserRole
  ) {
    await apiJson("/v1/admin/users/send-reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: emailAddress.trim(),
        purpose,
        role: targetRole,
      }),
    });
    addToast(
      "success",
      successTitle,
      `${purpose === "invite" ? "Invite" : "Reset"} email sent to ${emailAddress.trim()}`
    );
  }

  async function handleCreateUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;

    setSubmitting(true);
    try {
      await apiJson("/v1/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          role,
        }),
      });

      try {
        await sendReset(normalizedEmail, "invite", "User created", role);
      } catch (inviteError) {
        addToast(
          "info",
          "User created",
          inviteError instanceof Error
            ? `Account created, but the invite email failed: ${inviteError.message}`
            : "Account created, but the invite email failed."
        );
      }

      setEmail("");
      setRole("basic");
      await loadUsers();
    } catch (createError) {
      addToast(
        "error",
        "User creation failed",
        createError instanceof Error ? createError.message : "Could not create user"
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendReset(target: AdminUser) {
    const targetEmail = target.email?.trim();
    if (!targetEmail) return;

    setResettingUid(target.uid);
    try {
      await sendReset(targetEmail, "reset", "Reset email sent", target.role);
    } catch (resetError) {
      addToast(
        "error",
        "Reset failed",
        resetError instanceof Error ? resetError.message : "Could not send reset email"
      );
    } finally {
      setResettingUid(null);
    }
  }

  return (
    <section className="admin-users-section">
      <div className="admin-users-card">
        <div className="admin-users-header">
          <span className="section-title">User Provisioning</span>
          <span className="section-count">{userCountLabel}</span>
        </div>

        <form className="admin-users-form" onSubmit={handleCreateUser}>
          <input
            className="community-input"
            type="email"
            placeholder="user@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <select
            className="community-input"
            value={role}
            onChange={(e) => setRole(e.target.value === "admin" ? "admin" : "basic")}
          >
            <option value="basic">Basic User</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? "Creating..." : "Add User"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              void loadUsers();
            }}
            disabled={loading}
          >
            Refresh
          </button>
        </form>

        {error && <div className="community-warning">{error}</div>}

        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <span className="loading-text">Loading users</span>
          </div>
        ) : users.length === 0 ? (
          <div className="community-empty">No users provisioned yet.</div>
        ) : (
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Sign-In</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.uid}>
                    <td className="mono">{user.email ?? "—"}</td>
                    <td>{user.role}</td>
                    <td>{user.status}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{formatDate(user.lastSignInAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        disabled={!user.email || resettingUid === user.uid}
                        onClick={() => {
                          void handleResendReset(user);
                        }}
                      >
                        {resettingUid === user.uid ? "Sending..." : "Send Reset Email"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
